import React, { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { animated, useTransition } from "react-spring";
import styled from "styled-components";
import { pathBase } from "../auth/authConst";
import {
    canManageOrderers,
    canManageUnits,
} from "../common/helpers/municipalityRoles";
import { MatkontoTmpInfoProvider } from "../common/helpers/matkontoTmpInfoProider";
import mixins from "../common/mixins";
import { customerSupportInfo } from "../common/supportInfo";
import Header from "../components/Header";
import Main from "../components/Main";
import { ModalContext } from "../components/Modal";
import Navigation from "../components/Navigation";
import Plate from "../components/Plate";
import SupportInfo from "../components/SupportInfo";
import { AuthenticatedUserContext } from "../contexts/AuthenticatedUserContext";
import useSelectedMunicipalityId from "../hooks/useSelectedMunicipalityId";
import { useMunicipalityDetailsQuery } from "../queries/useMunicipalityDetailsQuery";
import { routes } from "../routes";
import AnimatedOutlet from "./AnimatedOutlet";

const Layout = () => {
    const location = useLocation();
    const { authUserData } = useContext(AuthenticatedUserContext);
    const { modalOptions } = useContext(ModalContext);
    const municipalityId = useSelectedMunicipalityId();

    const navigationItems = useMemo(() => {
        const customers = {
            title: "Slutkunder",
            path: routes.customers(municipalityId),
        } as const;
        const orderers = {
            title: "Beställare",
            path: routes.orderers(municipalityId),
        } as const;
        const units = {
            title: "Enheter",
            path: routes.units(municipalityId),
        } as const;

        const role = authUserData?.municipalityRole;

        return [
            customers,
            ...(role && canManageOrderers(role) ? [orderers] : []),
            ...(role && canManageUnits(role) ? [units] : []),
        ];
    }, [authUserData?.municipalityRole, municipalityId]);

    const transitions = useTransition(location, {
        exitBeforeEnter: true,
        initial: { opacity: 0, transform: "translate3d(1%, 0, 0)" },
        from: { opacity: 0, transform: "translate3d(1%, 0, 0)" },
        enter: {
            opacity: 1,
            transform: "translate3d(0, 0, 0)",
            animationDelay: "150",
            delay: 150,
        },
    });

    useEffect(() => {
        window.scroll({
            top: 0,
            left: 0,
        });
    }, [location]);

    useMatkontoCookieGuard();

    const { data: municipalityDetails, isSuccess: hasMunicipalityDetails } =
        useMunicipalityDetailsQuery();
    const allowedMunicipalities = authUserData?.authorisedMunicipalities;

    const showMunicipalityName =
        hasMunicipalityDetails &&
        (allowedMunicipalities === undefined ||
            allowedMunicipalities.length < 2);

    const municipalityName = hasMunicipalityDetails
        ? municipalityDetails.name
        : "Ombudshandel";

    return (
        <>
            <Header
                blur={modalOptions.visible}
                position="sticky"
                municipalityName={
                    showMunicipalityName ? municipalityName : undefined
                }>
                <Navigation
                    items={navigationItems}
                    municipalityId={municipalityId}
                    allowedMunicipalities={allowedMunicipalities}
                />
            </Header>
            <Main>
                <PageWrapper>
                    {transitions((style, item, t) => (
                        <Content
                            style={style}
                            blur={modalOptions.visible ? 1 : 0}
                            key={item.pathname}>
                            <AnimatedOutlet />
                        </Content>
                    ))}
                    <Plate padding="large">
                        <SupportInfo
                            heading={customerSupportInfo.heading}
                            phone={customerSupportInfo.phone}
                            openingHoursLabel={
                                customerSupportInfo.openingHoursLabel
                            }
                            openingHours={customerSupportInfo.openingHours}
                            align="left"
                        />
                    </Plate>
                </PageWrapper>
            </Main>
        </>
    );
};

export default Layout;

const PageWrapper = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 24px;
    justify-content: space-between;
`;

export const Content = styled(animated.div)<{ blur?: number }>`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    transition: filter ${mixins.transitions.large};
    filter: ${({ blur }) => (blur ? "blur(4px)" : "none")};
`;

const useMatkontoCookieGuard = () => {
    const location = useLocation();

    // the only locations that can have the tmp values are base path (before firing react router), and Matkonto callback url
    const locationIsAllowed = () => {
        return (
            location.pathname.includes(routes.matkontoCallback) ||
            location.pathname === pathBase
        );
    };

    if (MatkontoTmpInfoProvider.exists() && !locationIsAllowed()) {
        MatkontoTmpInfoProvider.remove();
    }
};
